import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Message, Placeholder } from 'rsuite';
import { ConfirmModal } from '@nsi-advisory-services/components';
import Section from './Section';
import ReminderTimeline from '../timeoff/ReminderTimeline';
import { MAX_DAYS, MAX_STEPS, isValidDays, scheduleReminders } from '../timeoff/reminderSchedule';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import { showToast } from '../../utils/toast';
import { useSaveReminderConfig, useReminderConfig } from '../../hooks/useQueries';
import '../timeoff/ApprovalReminderSettings.css';

let nextId = 1;
const withId = (step) => ({ ...step, id: nextId++ });

const toSteps = (notifications, definition) => {
  const list = Array.isArray(notifications) ? notifications : definition.defaults;
  return list.map((step) => {
    const type = definition.stepTypes[step?.type] ? step.type : definition.defaults[0].type;
    const rule = definition.stepTypes[type];
    return withId({ type, days: rule.needsDays ? Math.max(rule.minDays ?? 1, Math.round(Number(step?.days) || 0)) : 0 });
  });
};

const signature = (steps) => JSON.stringify(steps.map(({ type, days }) => [type, days]));

export default function ReminderSettings({ definition }) {
  const configQuery = useReminderConfig(definition.scope);
  const saveConfig = useSaveReminderConfig(definition.scope);
  const headingId = useId();
  const stepTypes = definition.stepTypes;

  const saved = useMemo(() => toSteps(configQuery.data?.config?.notifications, definition), [configQuery.data, definition]);
  const [steps, setSteps] = useState(saved);
  const [lead, setLead] = useState(14);
  const [focusId, setFocusId] = useState(null);
  const selectRefs = useRef({});

  useEffect(() => setSteps(saved), [saved]);
  useEffect(() => {
    if (focusId == null) return;
    selectRefs.current[focusId]?.focus();
    setFocusId(null);
  }, [focusId]);

  const rows = useMemo(() => scheduleReminders(steps, lead), [steps, lead]);
  const dirty = signature(steps) !== signature(saved);
  const invalid = (!definition.allowEmpty && steps.length === 0) || steps.some((step) => !isValidDays(step, stepTypes));
  const guard = useUnsavedChangesGuard(dirty);

  const updateStep = (id, patch) => setSteps((prev) => prev.map((step) => (step.id === id ? { ...step, ...patch } : step)));

  const changeType = (step, type) => {
    const days = stepTypes[type].needsDays ? Math.max(stepTypes[type].minDays ?? 1, step.days || 0) : 0;
    updateStep(step.id, { type, days });
  };

  const addStep = () => {
    const step = withId({ type: definition.survey ? 'days_before_close' : 'after_days', days: 3 });
    setSteps((prev) => [...prev, step]);
    setFocusId(step.id);
  };

  const save = async () => {
    try {
      await saveConfig.mutateAsync({
        ...definition.payload,
        notifications: steps.map(({ type, days }) => (type === 'immediate' ? { type } : { type, days })),
      });
      showToast('success', 'Reminder schedule saved.');
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save the schedule'));
    }
  };

  if (configQuery.isLoading) return <Placeholder.Paragraph rows={6} active />;
  if (configQuery.error) {
    return <Message type="error" showIcon>{String(configQuery.error?.message || configQuery.error)}</Message>;
  }

  return (
    <Section
      title={definition.title}
      aside={(
        <div className="ar-actions">
          {dirty ? <span className="ar-dirty">Unsaved changes</span> : null}
          <button
            type="button"
            className="bdc-btn bdc-btn--primary"
            onClick={save}
            disabled={!dirty || invalid || saveConfig.isPending}
          >
            {saveConfig.isPending ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      )}
    >
      <p className="ar-subtitle">{definition.description}</p>

      <ReminderTimeline rows={rows} lead={lead} onLeadChange={setLead} survey={definition.survey} />

      <section className="ar-card" aria-labelledby={headingId}>
        <div className="ar-steps__head">
          <h3 id={headingId} className="ar-card__title">Reminder emails</h3>
          <span className="ar-muted">{steps.length} {steps.length === 1 ? 'reminder' : 'reminders'}</span>
        </div>

        {steps.length === 0 ? (
          <p className="ar-empty">{definition.allowEmpty ? 'Survey reminders are turned off.' : 'No reminders. Add one to save; approvers need at least one email for pending requests.'}</p>
        ) : (
          <ol className="ar-steps">
            {rows.map((row) => {
              const type = stepTypes[row.type];
              const number = row.index + 1;
              return (
                <li key={row.id} className={`ar-step${row.late || row.early ? ' ar-step--off' : ''}`}>
                  <span className="ar-num">{number}</span>
                  <div className="ar-sentence">
                    <select
                      ref={(node) => { selectRefs.current[row.id] = node; }}
                      className="form-select ar-select"
                      value={row.type}
                      onChange={(event) => changeType(row, event.target.value)}
                      aria-label={`When to send reminder ${number}`}
                    >
                      {Object.entries(stepTypes).map(([value, option]) => (
                        <option key={value} value={value}>{option.label}</option>
                      ))}
                    </select>
                    {type.needsDays ? (
                      <input
                        type="number"
                        className="form-control ar-days"
                        min={type.minDays ?? 1}
                        max={MAX_DAYS}
                        inputMode="numeric"
                        value={Number.isFinite(row.days) ? row.days : ''}
                        onChange={(event) => updateStep(row.id, { days: parseInt(event.target.value, 10) })}
                        aria-label={`Number of days for reminder ${number}`}
                        aria-invalid={!isValidDays(row, stepTypes)}
                      />
                    ) : null}
                    <span className="ar-muted">{type.suffix(row.days)}</span>
                  </div>
                  <button
                    type="button"
                    className="ar-icon-btn"
                    onClick={() => setSteps((prev) => prev.filter((step) => step.id !== row.id))}
                    aria-label={`Remove reminder ${number}`}
                  >
                    <i className="bi bi-trash" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ol>
        )}

        <div className="ar-add">
          <button type="button" onClick={addStep} disabled={steps.length >= MAX_STEPS}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add reminder
          </button>
        </div>
      </section>

      <ConfirmModal
        open={!!guard.pendingTo}
        title="Leave without saving?"
        body="The reminder schedule has unsaved changes. Leaving now puts it back the way it was."
        confirmText="Leave without saving"
        cancelText="Stay here"
        danger
        onConfirm={guard.leave}
        onCancel={guard.stay}
      />
    </Section>
  );
}